<template>
  <div class="l-main">
    <div class="c-language-bar">
      <span class="c-language-bar__label">Language</span>
      <app-tabs
        variant="pills"
        label="Language"
        :tabs="languageTabs"
        :value="currentLanguage.code"
        @input="changeLanguageByCode"
      />
    </div>

    <div class="l-main__ctn">
      <div
        class="c-filter-bar"
      >
        <label class="c-filter-bar__field c-filter-bar__field--wide">
          <span>Search</span>
          <span class="ui-field__control ui-field__control--with-action">
            <app-icon
              class="ui-field__icon"
              name="search"
            />
            <input
              v-model="searchQuery"
              class="ui-field__input"
              type="text"
              placeholder="Casino category name or ID"
              @keydown.enter="searchSeo(true)"
            >
            <button
              v-if="searchQuery"
              type="button"
              class="ui-field__action"
              title="Clear search"
              aria-label="Clear search"
              @click="resetSearch()"
            >
              <app-icon name="close" />
            </button>
          </span>
        </label>

        <button
          type="button"
          class="ui-btn ui-btn--primary"
          :disabled="searchQuery.length < 1"
          @click="searchSeo(true)"
        >
          <app-icon name="search" />
          Search
        </button>
      </div>

      <div class="c-table-wrapper l-casino-category-seo__table">
        <base-table-component
          :prop-data="seoRulesTableData"
          sortable
          :empty-title="fetchedResults ? 'No casino categories found' : 'No casino categories'"
          :empty-description="fetchedResults ? 'Try another name or ID.' : 'There is nothing to show here yet.'"
          @sort="handleSort"
        >
          <template v-slot:default="slotProps">
            <span class="c-table__btn-wrap">
              <generic-button
                icon
                transparent
                text-inherit
                @click="editSeoRule(slotProps.selected.seoRule)"
              >
                <md-tooltip md-direction="top">
                  Edit SEO rule
                </md-tooltip>
                <img src="../../../../../assets/edit-button.svg" alt="">
              </generic-button>
            </span>
          </template>
        </base-table-component>
      </div>

      <paginate
        v-if="totalPages > 1"
        v-model="currentPage"
        :page-count="totalPages"
        :prev-text="'‹'"
        :next-text="'›'"
        :page-range="5"
        :margin-pages="1"
        :break-view-link-class="'md-button'"
        :click-handler="searchSeo"
        :container-class="'c-pagination'"
        :prev-link-class="'md-button prev md-elevation-1'"
        :next-link-class="'md-button next md-elevation-1'"
        :page-link-class="'md-button md-elevation-1'"
        :active-class="'active'"
        :disabled-class="'disabled'"
      />
    </div>
  </div>
</template>

<script>
import GenericButton from '../../../../../shared/buttons/GenericButton';
import AppIcon from '@/shared/app-icon/AppIcon';
import AppTabs from '@/shared/app-tabs/AppTabs';
import { jsonParse } from '@/helpers/transformers'
import { seoRulesService } from '@/services/seo-rules'
import get from 'lodash/get'
import { languageService } from '@/services/language'
import BaseTableComponent from '@/shared/table/BaseTableComponent'
import EditSeoRuleModal from './edit'
import {casinoCategoriesService} from "@/services/casino-category";
import { mapActions } from 'vuex';

export default {
  name: "CasinoCategories",

  components: {
    GenericButton,
    BaseTableComponent,
    AppIcon,
    AppTabs,
  },

  props: {
    website: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      searchQuery: "",
      searchSportQuery: "",
      searchRegionQuery: "",
      selectedFilter: 'casino-category',
      searchItems: [],
      fetchedResults: false,
      languages: [],
      currentLanguage: {},
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      defaultLanguageId: null,
      sortColumnActive: '',
      sortColumnDirection: '',
    };
  },

  computed: {
    languageTabs() {
      return this.languages.map(language => ({ key: language.code, label: String(language.code).toUpperCase() }));
    },

    // only rules whose casino category belongs to this website: rules without a target
    // (category no longer synced) or with another website's target are not listed
    websiteSeoRules() {
      const websiteId = this.website ? String(this.website.id) : null;

      return (this.searchItems || []).filter(seoRule => {
        return seoRule.target && String(seoRule.target.websiteId) === websiteId;
      });
    },

    seoRulesTableData() {
      return this.websiteSeoRules.map(seoRule => ({
        seoRule: seoRule,
        rowData: {
          name: seoRule.translation,
          ...this._getEntities(seoRule),
          id: this._getEntityId(seoRule),
        }
      }));
    },
  },

  created() {
    this.getAvailableLanguages();
    this.searchCasinoSeo();
  },

  methods: {
    ...mapActions("response", ["setGlobalError"]),
    async searchSeo(page = 1) {
      this.currentPage = typeof page !== 'number' ? 1 : page;

      const searchConstraints = {
        ...(this.searchQuery ? { term: this.searchQuery} : {}),
        ...(this.selectedFilter ? { filter: this.selectedFilter} : {}),
        ...(this.currentPage ? { page: this.currentPage} : {}),
        ...(this.defaultLanguageId ? { defaultLanguageId: this.defaultLanguageId} : {}),
        ...(this.sortColumnActive ? { sortColumn: this.sortColumnActive } : {}),
        ...(this.sortColumnDirection ? { sortDirection: this.sortColumnDirection } : {}),
        ...(this.website ? { websiteId: this.website.id} : {}),
      }

      const searchResponse = await seoRulesService.search(this.currentLanguage.id, searchConstraints)

      const {items, pageCount} = searchResponse.data;

      this.searchItems = items;
      this.totalPages = pageCount;

      this.fetchedResults = true;
    },

    async searchCasinoSeo() {
      const response = await seoRulesService.getCasinoCategories(this.website)
      const categories = response.data.payload;

      try {
        await casinoCategoriesService.create(categories, this.website.id);
      } catch (error) {
        this.setGlobalError(error.data.message);
      }

      return this.searchSeo()
    },

    changeLanguageByCode(code) {
      this.changeLanguage(this.languages.find(language => language.code === code));
    },

    changeLanguage(language) {
      this.currentLanguage = language;
      this.searchSeo();
    },

    async getAvailableLanguages() {
      const allLanguagesResponse = await languageService.getAllOnlineLanguages();
      const allOnlineLanguages = allLanguagesResponse.data.items;

      const additionalLanguages = jsonParse(get(this, 'website.betshop.market.additionalLanguages')) || [];
      const defaultLanguage = get(this, 'website.betshop.market.defaultLanguage', 'en');

      const betshopLanguageCodes = [...new Set([defaultLanguage, ...additionalLanguages])];

      this.languages = allOnlineLanguages.filter(onlineLanguage => betshopLanguageCodes.includes(onlineLanguage.code))
      this.currentLanguage = this.languages[0];
      this.defaultLanguageId = this.languages.find(language => language.code === 'en')?.id
    },

    editSeoRule(seoRule) {
      this.$modal.show(
        EditSeoRuleModal,
        {
          seoRule,
          website: this.website,
          language: this.currentLanguage
        },
        { height: 'auto', scrollable: true, width: '80%' }
      );
    },

    _getEntities(seoRule) {
      const entities = {
        games: '',
        active: '',
        hidden: '',
      }

      // some rules come without `target` (category no longer synced): show '-' instead of breaking the whole table
      const target = seoRule.target || {};

      entities.active = 'active' in target ? target.active : '-';
      entities.hidden = 'hidden' in target ? target.hidden : '-';
      entities.games = 'numberOfGames' in target ? target.numberOfGames : '-';

      return entities;
    },

    _getEntityId(seoRule) {
      switch (seoRule.entityType) {
        case 'CasinoCategoryEntity':
          return seoRule.target ? seoRule.target.casinoCategoryId : '-';
        default:
          return '-'
      }
    },

    handleSort(column, direction) {
      this.sortColumnActive = column;
      this.sortColumnDirection = direction;
      this.searchSeo(this.currentPage);
    },

    resetSearch() {
      this.searchQuery = '';
      this.searchSeo(true);
    }
  },
};
</script>

<style lang="scss" scoped>
  .c-search {
    padding: 0 0 8px;
  }

  .c-search-filter-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .u-mr-md {
      margin-right: 16px !important;
    }
  }

  .c-search-additional-fields {
    display: flex;
    align-items: baseline;

    > div {
      margin-right: 10px;
    }
  }

  .c-search-result-not-found {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 25px;
    margin-top: 100px;
  }
</style>
